import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Users, Circuitry, Buildings, Plus, Spinner, House, Eye, Pencil,
  WifiHigh, WifiSlash, ShieldWarning, ClockCounterClockwise, ArrowRight,
  Lightning, Plugs, ChartLineUp, CloudArrowDown, MagnifyingGlass, Check, X,
  Gear, PlugsConnected, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [pulsar, setPulsar] = useState(null);
  const [cloudStats, setCloudStats] = useState(null);
  const [cloudDevices, setCloudDevices] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(false);
  const [cloudFilter, setCloudFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [registerTarget, setRegisterTarget] = useState(null);
  const [registerCustomerId, setRegisterCustomerId] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerLoading, setRegisterLoading] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [bulkCustomerId, setBulkCustomerId] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [systemSettings, setSystemSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [tuyaSettings, setTuyaSettings] = useState(null);
  const [tuyaTest, setTuyaTest] = useState(null);
  const [tuyaTestLoading, setTuyaTestLoading] = useState(false);
  const [tuyaEditOpen, setTuyaEditOpen] = useState(false);
  const [tuyaForm, setTuyaForm] = useState({ access_id: "", access_secret: "", endpoint: "", app_schema: "smartlife", linked_uid: "" });
  const [tuyaSaving, setTuyaSaving] = useState(false);
  const [uidDetecting, setUidDetecting] = useState(false);
  // Email settings state
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailEditOpen, setEmailEditOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ smtp_email: "", smtp_password: "", smtp_host: "smtp.gmail.com", smtp_port: 587, sender_name: "NANOMET", enabled: true });
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, customersRes, pulsarRes, sysRes, tuyaRes, emailRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/customers`),
        axios.get(`${API_URL}/api/admin/pulsar-status`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/system-settings`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/tuya-settings`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/email-settings`).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setCustomers(customersRes.data);
      setPulsar(pulsarRes.data);
      setSystemSettings(sysRes.data);
      if (tuyaRes.data) setTuyaSettings(tuyaRes.data);
      if (emailRes.data) setEmailSettings(emailRes.data);
    } catch { toast.error("Veriler yuklenirken hata olustu"); }
    finally { setLoading(false); }
  }, []);

  // Tuya cloud stats — sadece sayfa ilk yuklenmesinde, ayri olarak (cooldown nedeniyle)
  const fetchCloudStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/tuya-cloud-stats`).catch(() => ({ data: null }));
      setCloudStats(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); fetchCloudStats(); }, [fetchData, fetchCloudStats]);

  const fetchCloudDevices = async () => {
    setCloudLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/tuya-cloud-devices`);
      setCloudDevices(res.data);
      if (res.data.total_cloud_devices === 0) {
        toast.info("Cloud'da cihaz bulunamadi. Tuya API bilgilerinizi kontrol edin.");
      } else {
        toast.success(`${res.data.total_cloud_devices} cihaz bulundu`);
      }
    } catch (err) {
      // 429 = rate limit — sessizce atla, kullaniciya gereksiz hata gosterme
      if (err?.response?.status !== 429) {
        toast.error(getErrorMessage(err));
      }
    }
    finally { setCloudLoading(false); }
  };

  const handleTuyaTest = async () => {
    setTuyaTestLoading(true);
    setTuyaTest(null);
    try {
      const res = await axios.post(`${API_URL}/api/admin/tuya-test-connection`);
      setTuyaTest(res.data);
      if (res.data.connection_ok) {
        toast.success("Tuya baglantisi basarili!");
      } else {
        toast.error("Tuya baglanti hatasi - detaylar asagida");
      }
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setTuyaTestLoading(false); }
  };

  const handleSaveTuyaSettings = async () => {
    setTuyaSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/tuya-settings`, tuyaForm);
      toast.success("Tuya ayarlari kaydedildi");
      setTuyaEditOpen(false);
      // Tuya settings'i yeniden al, tuya-cloud-stats'i cooldown nedeniyle atla
      try {
        const r = await axios.get(`${API_URL}/api/admin/tuya-settings`);
        if (r.data) setTuyaSettings(r.data);
      } catch {}
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setTuyaSaving(false); }
  };

  const handleRegisterDevice = async () => {
    if (!registerTarget || !registerCustomerId || !registerName) {
      toast.error("Tum alanlari doldurun");
      return;
    }
    setRegisterLoading(true);
    try {
      await axios.post(`${API_URL}/api/devices?customer_id=${registerCustomerId}`, {
        tuya_device_id: registerTarget.tuya_device_id,
        name: registerName,
      });
      toast.success("Cihaz sisteme basariyla kaydedildi");
      setRegisterDialogOpen(false);
      setRegisterTarget(null);
      setRegisterName("");
      fetchCloudDevices();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setRegisterLoading(false); }
  };

  const handleBulkRegister = async () => {
    if (selectedDevices.size === 0) { toast.error("Cihaz seciniz"); return; }
    if (!bulkCustomerId) { toast.error("Musteri seciniz"); return; }
    setBulkLoading(true);
    try {
      const devices = cloudDevices.devices
        .filter((d) => selectedDevices.has(d.tuya_device_id))
        .map((d) => ({ tuya_device_id: d.tuya_device_id, name: d.name || d.tuya_device_id }));
      const res = await axios.post(`${API_URL}/api/devices/bulk-register`, {
        customer_id: bulkCustomerId,
        devices,
      });
      const { success_count, failed_count } = res.data;
      if (success_count > 0) toast.success(`${success_count} cihaz basariyla kaydedildi`);
      if (failed_count > 0) toast.warning(`${failed_count} cihaz kaydedilemedi`);
      setSelectedDevices(new Set());
      setBulkCustomerId("");
      fetchCloudDevices();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setBulkLoading(false); }
  };

  const toggleDeviceSelection = (tuyaId) => {
    setSelectedDevices((prev) => {
      const next = new Set(prev);
      if (next.has(tuyaId)) next.delete(tuyaId);
      else next.add(tuyaId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!cloudDevices) return;
    const unregistered = cloudDevices.devices.filter((d) => !d.registered);
    if (selectedDevices.size === unregistered.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(unregistered.map((d) => d.tuya_device_id)));
    }
  };

  const handleSaveSystemSettings = async () => {
    setSettingsLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/system-settings`, systemSettings);
      toast.success("Sistem ayarlari kaydedildi");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSettingsLoading(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: t("customers.title"), value: stats?.customers_count || 0, icon: Users, bg: "bg-indigo-50", text: "text-indigo-600" },
          { label: t("sidebar.apartments"), value: stats?.apartments_count || 0, icon: Buildings, bg: "bg-emerald-50", text: "text-emerald-600" },
          { label: t("units.title"), value: stats?.units_count || 0, icon: House, bg: "bg-amber-50", text: "text-amber-600" },
          { label: t("devices.title"), value: stats?.devices_count || 0, icon: Circuitry, bg: "bg-purple-50", text: "text-purple-600" },
          { label: t("tenants.title"), value: stats?.tenants_count || 0, icon: Users, bg: "bg-rose-50", text: "text-rose-600" },
          { label: t("dashboard.total_balance"), value: `₺${stats?.total_balance?.toFixed(0) || 0}`, icon: Users, bg: "bg-sky-50", text: "text-sky-600" },
        ].map(({ label, value, icon: Icon, bg, text }) => (
          <Card key={label} className="border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${bg} rounded-xl flex items-center justify-center`}>
                  <Icon size={20} weight="duotone" className={text} />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-500">{label}</p>
                  <p className="text-xl font-bold text-slate-900">{value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pulsar & System Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pulsar Connection */}
        <Card className="border-slate-200 shadow-sm" data-testid="pulsar-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Plugs size={18} className="text-indigo-600" /> {t("dashboard.pulsar_status")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl border-2 transition-all"
              style={{
                backgroundColor: pulsar?.pulsar_running ? "#F0FDF4" : "#FEF2F2",
                borderColor: pulsar?.pulsar_running ? "#86EFAC" : "#FECACA",
              }}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${pulsar?.pulsar_running ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: pulsar?.pulsar_running ? "#166534" : "#991B1B" }}>
                    {pulsar?.pulsar_running ? t("common.online") : t("common.offline")}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">{pulsar?.endpoint || "-"}</p>
                </div>
              </div>
              <Badge className={pulsar?.pulsar_running ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                {pulsar?.pulsar_running ? t("common.active").toUpperCase() : t("common.inactive").toUpperCase()}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-indigo-50 rounded-xl text-center">
                <p className="text-xs text-indigo-600 font-medium">{t("dashboard.event_mode")}</p>
                <p className="text-sm font-bold text-indigo-900 mt-1">Pulsar Real-Time</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl text-center">
                <p className="text-xs text-slate-500 font-medium">{t("dashboard.backup_polling")}</p>
                <p className="text-sm font-bold text-slate-700 mt-1">{pulsar?.polling_interval_seconds ? `${Math.round(pulsar.polling_interval_seconds / 60)} dk` : "-"}</p>
              </div>
            </div>

            {/* Dead Letter Warning */}
            {(pulsar?.dead_letter_count || 0) > 0 && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-xs font-semibold text-red-700">Dead Letter: {pulsar.dead_letter_count} cihaz kontrol edilemiyor</p>
                {pulsar.dead_letter_devices?.map((d, i) => (
                  <p key={i} className="text-[10px] text-red-500 mt-1">{d.device_name} - {d.failed_at?.slice(0, 16)}</p>
                ))}
              </div>
            )}

            {/* Polling Mode Indicator */}
            {pulsar?.polling_mode === "emergency" && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl animate-pulse">
                <p className="text-xs font-semibold text-amber-700">{t("dashboard.emergency_mode")}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Event Comparison */}
        <Card className="border-slate-200 shadow-sm" data-testid="event-comparison-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ChartLineUp size={18} className="text-emerald-600" /> {t("dashboard.arch_compare")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2">
                  <Lightning size={18} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">{t("dashboard.new_event")}</span>
                </div>
                <span className="text-xs text-emerald-600 font-semibold">{"< 1 sn gecikme"}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 opacity-60">
                <div className="flex items-center gap-2">
                  <ClockCounterClockwise size={18} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-500 line-through">{t("dashboard.old_polling")}</span>
                </div>
                <span className="text-xs text-slate-400">10 dk gecikme</span>
              </div>

              <div className="mt-4 p-4 bg-indigo-50 rounded-xl">
                <p className="text-sm font-semibold text-indigo-900 mb-2">{t("dashboard.last_24h")}</p>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-2xl font-bold text-indigo-600">{pulsar?.pulsar_alerts_24h || 0}</p>
                    <p className="text-[10px] text-indigo-500">Pulsar Event</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{pulsar?.batch_alerts_24h || 0}</p>
                    <p className="text-[10px] text-amber-500">Batch Alert</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-red-600">{pulsar?.dead_letter_count || 0}</p>
                    <p className="text-[10px] text-red-500">Dead Letter</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{stats?.devices_count || 0}</p>
                    <p className="text-[10px] text-emerald-500">{t("dashboard.total_devices")}</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sistem Ayarlari */}
      {systemSettings && (
        <Card className="border-slate-200 shadow-sm" data-testid="system-settings-card">
          <CardHeader>
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Lightning size={18} className="text-amber-600" /> {t("dashboard.system_settings")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{t("dashboard.fixed_fee_system")}</p>
                    <p className="text-xs text-slate-500">{t("dashboard.fixed_fee_desc")}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer"
                      checked={systemSettings.fixed_fee_enabled || false}
                      onChange={(e) => setSystemSettings({...systemSettings, fixed_fee_enabled: e.target.checked})}
                      data-testid="system-fixed-fee-toggle" />
                    <div className="w-9 h-5 bg-slate-300 peer-checked:bg-emerald-500 rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
                  </label>
                </div>
                {systemSettings.fixed_fee_enabled && (
                  <div className="space-y-2">
                    <Label className="text-xs">{t("dashboard.min_fixed_fee")}</Label>
                    <Input type="number" min="0" step="1" className="h-8 text-sm"
                      value={systemSettings.fixed_fee_min_amount || ""}
                      onChange={(e) => setSystemSettings({...systemSettings, fixed_fee_min_amount: parseFloat(e.target.value) || 0})}
                      placeholder="orn: 50"
                      data-testid="system-fixed-fee-min" />
                    <p className="text-xs text-slate-400">{t("dashboard.min_fixed_fee_desc")}</p>
                  </div>
                )}
              </div>
              <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                <div className="space-y-2">
                  <Label className="text-xs">{t("dashboard.debt_cutoff")}</Label>
                  <Input type="number" min="1" max="28" className="h-8 text-sm"
                    value={systemSettings.debt_cutoff_day || 15}
                    onChange={(e) => setSystemSettings({...systemSettings, debt_cutoff_day: parseInt(e.target.value) || 15})}
                    data-testid="debt-cutoff-day" />
                  <p className="text-xs text-slate-400">{t("dashboard.debt_cutoff_desc")}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={handleSaveSystemSettings} disabled={settingsLoading}
                data-testid="save-system-settings">
                {settingsLoading ? t("billing.saving") : t("billing.save_settings")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tuya Cloud Stats + Device Discovery */}
      <Card className="border-slate-200 shadow-sm" data-testid="tuya-cloud-card">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <CloudArrowDown size={18} className="text-blue-600" /> {t("dashboard.tuya_cloud")}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCloudDevices}
            disabled={cloudLoading}
            data-testid="discover-devices-btn"
          >
            {cloudLoading ? <Spinner size={16} className="animate-spin mr-2" /> : <MagnifyingGlass size={16} className="mr-2" />}
            {cloudLoading ? t("common.loading") : t("common.search")}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Tuya Connection Info */}
          <div className="p-4 bg-slate-50 rounded-xl space-y-3" data-testid="tuya-connection-info">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                <PlugsConnected size={16} className="text-blue-600" /> {t("dashboard.api_info")}
              </h4>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleTuyaTest} disabled={tuyaTestLoading}
                  data-testid="tuya-test-btn">
                  {tuyaTestLoading ? <Spinner size={12} className="animate-spin mr-1" /> : <PlugsConnected size={12} className="mr-1" />}
                  {t("dashboard.connection_test")}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
                  setTuyaForm({
                    access_id: tuyaSettings?.access_id || "",
                    access_secret: "",
                    endpoint: tuyaSettings?.endpoint || "",
                    app_schema: tuyaSettings?.app_schema || "smartlife",
                    linked_uid: tuyaSettings?.linked_uid || "",
                  });
                  setTuyaEditOpen(true);
                }} data-testid="tuya-edit-btn">
                  <Gear size={12} className="mr-1" /> {t("common.edit")}
                </Button>
              </div>
            </div>
            {tuyaSettings && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-xs text-slate-400">Access ID</p>
                  <p className="font-mono text-slate-700">{tuyaSettings.access_id_masked}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Access Secret</p>
                  <p className="font-mono text-slate-700">{tuyaSettings.access_secret_masked}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Endpoint</p>
                  <p className="font-mono text-slate-700">{tuyaSettings.endpoint}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">{t("dashboard.app_type")}</p>
                  <Badge className="bg-blue-100 text-blue-700">{tuyaSettings.app_schema || "smartlife"}</Badge>
                </div>
                <div className="md:col-span-2">
                  <p className="text-xs text-slate-400">{t("dashboard.linked_uid")}</p>
                  <p className="font-mono text-slate-700 text-xs">{tuyaSettings.linked_uid || <span className="text-amber-500 italic">{t("dashboard.not_set")}</span>}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">{t("dashboard.source_label")}</p>
                  <Badge className={tuyaSettings.source === "database" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}>
                    {tuyaSettings.source === "database" ? t("dashboard.database") : t("dashboard.env_var")}
                  </Badge>
                </div>
              </div>
            )}
            {/* Connection Test Results */}
            {tuyaTest && (
              <div className={`p-3 rounded-lg border ${tuyaTest.connection_ok ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                <p className={`text-sm font-bold mb-2 ${tuyaTest.connection_ok ? "text-emerald-700" : "text-red-700"}`}>
                  {tuyaTest.connection_ok ? t("dashboard.connection_ok") : t("dashboard.connection_fail")}
                </p>
                <div className="space-y-1">
                  {tuyaTest.steps.map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      {step.success ? <Check size={14} className="text-emerald-600" /> : <X size={14} className="text-red-500" />}
                      <span className="font-medium">{step.step}:</span>
                      <span className="text-slate-500">{step.detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Tuya Edit Dialog */}
          {tuyaEditOpen && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <Warning size={16} className="text-amber-600" />
                <h4 className="text-sm font-bold text-amber-800">{t("dashboard.update_api")}</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Access ID</Label>
                  <Input className="h-8 text-sm font-mono" value={tuyaForm.access_id}
                    onChange={(e) => setTuyaForm({ ...tuyaForm, access_id: e.target.value })}
                    data-testid="tuya-access-id-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Access Secret</Label>
                  <Input className="h-8 text-sm font-mono" type="password" value={tuyaForm.access_secret}
                    placeholder="Degistirmek icin yeni deger girin"
                    onChange={(e) => setTuyaForm({ ...tuyaForm, access_secret: e.target.value })}
                    data-testid="tuya-access-secret-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Endpoint URL</Label>
                  <Input className="h-8 text-sm font-mono" value={tuyaForm.endpoint}
                    onChange={(e) => setTuyaForm({ ...tuyaForm, endpoint: e.target.value })}
                    data-testid="tuya-endpoint-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("dashboard.app_schema")}</Label>
                  <select className="h-8 w-full text-sm border rounded px-2 bg-white"
                    value={tuyaForm.app_schema}
                    onChange={(e) => setTuyaForm({ ...tuyaForm, app_schema: e.target.value })}
                    data-testid="tuya-schema-select">
                    <option value="smartlife">Smart Life</option>
                    <option value="tuyaSmart">Tuya Smart</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{t("dashboard.linked_uid")} (Smart Life UID)</Label>
                  <Button size="sm" variant="outline" className="h-6 text-xs px-2" disabled={uidDetecting}
                    onClick={async () => {
                      setUidDetecting(true);
                      try {
                        const res = await axios.post(`${API_URL}/api/admin/tuya-detect-uid`, {});
                        if (res.data.success) {
                          setTuyaForm(f => ({ ...f, linked_uid: res.data.uids.join(",") }));
                          toast.success(`${res.data.uids.length} UID tespit edildi, ${res.data.total_devices} cihaz bulundu`);
                        } else {
                          toast.error(res.data.message);
                        }
                      } catch { toast.error("UID tespit hatasi"); }
                      finally { setUidDetecting(false); }
                    }}
                    data-testid="tuya-detect-uid-btn">
                    {uidDetecting ? t("dashboard.detecting") : t("dashboard.auto_detect")}
                  </Button>
                </div>
                <Input className="h-8 text-sm font-mono" value={tuyaForm.linked_uid}
                  placeholder="Tuya IoT Platform > Cloud > Linked Devices adresinden UID'nizi girin"
                  onChange={(e) => setTuyaForm({ ...tuyaForm, linked_uid: e.target.value })}
                  data-testid="tuya-linked-uid-input" />
                <p className="text-xs text-slate-400">Tuya IoT Platform &gt; Cloud &gt; Bagli Cihazlar sayfasindan Smart Life hesabinizin UID'sini kopyalayip yapistiriniz. Birden fazla UID icin virgul ile ayirin.</p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setTuyaEditOpen(false)}>{t("common.cancel")}</Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={handleSaveTuyaSettings} disabled={tuyaSaving}
                  data-testid="tuya-save-btn">
                  {tuyaSaving ? t("billing.saving") : t("common.save")}
                </Button>
              </div>
            </div>
          )}

          {/* Cloud Stats Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="p-3 bg-blue-50 rounded-xl text-center">
              <p className="text-xs text-blue-600 font-medium">{t("dashboard.cloud_total")}</p>
              <p className="text-2xl font-bold text-blue-900">{cloudStats?.cloud_total_devices ?? "-"}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-xl text-center">
              <p className="text-xs text-emerald-600 font-medium">{t("dashboard.registered")}</p>
              <p className="text-2xl font-bold text-emerald-900">{cloudStats?.system_registered_devices ?? "-"}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl text-center">
              <p className="text-xs text-amber-600 font-medium">{t("dashboard.unregistered")}</p>
              <p className="text-2xl font-bold text-amber-900">{cloudStats?.unregistered_devices ?? "-"}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl text-center">
              <p className="text-xs text-green-600 font-medium">Online</p>
              <p className="text-2xl font-bold text-green-900">{cloudStats?.system_online_devices ?? "-"}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-xl text-center">
              <p className="text-xs text-slate-500 font-medium">Offline</p>
              <p className="text-2xl font-bold text-slate-700">{cloudStats?.system_offline_devices ?? "-"}</p>
            </div>
          </div>

          {/* Cloud Devices Table */}
          {cloudDevices && (
            <>
              <div className="flex items-center gap-3 pt-2">
                <p className="text-sm font-medium text-slate-700">
                  Bulunan: <span className="font-bold">{cloudDevices.total_cloud_devices}</span> cihaz
                  ({cloudDevices.registered_count} kayitli, {cloudDevices.unregistered_count} kayitsiz)
                </p>
                <div className="flex gap-1 ml-auto">
                  {[
                    { key: "all", label: t("dashboard.all") },
                    { key: "unregistered", label: t("dashboard.unregistered") },
                    { key: "registered", label: t("dashboard.registered") },
                  ].map((f) => (
                    <Button
                      key={f.key}
                      size="sm"
                      variant={cloudFilter === f.key ? "default" : "outline"}
                      className={cloudFilter === f.key ? "bg-blue-600 hover:bg-blue-700 text-xs" : "text-xs"}
                      onClick={() => setCloudFilter(f.key)}
                      data-testid={`cloud-filter-${f.key}`}
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto max-h-96">
                {/* Bulk Action Bar */}
                {selectedDevices.size > 0 && (
                  <div className="flex items-center gap-3 p-3 mb-2 bg-blue-50 border border-blue-200 rounded-xl" data-testid="bulk-action-bar">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-600 text-white text-sm px-3 py-1">{selectedDevices.size} cihaz secildi</Badge>
                      <Button size="sm" variant="ghost" className="text-xs text-slate-500 h-7" onClick={() => setSelectedDevices(new Set())} data-testid="bulk-clear-selection">
                        <X size={14} className="mr-1" /> Temizle
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      <Select value={bulkCustomerId} onValueChange={setBulkCustomerId}>
                        <SelectTrigger className="w-64 h-8 text-xs" data-testid="bulk-customer-select">
                          <SelectValue placeholder="Musteri sec..." />
                        </SelectTrigger>
                        <SelectContent>
                          {customers.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-xs h-8 px-4"
                        onClick={handleBulkRegister}
                        disabled={bulkLoading || !bulkCustomerId}
                        data-testid="bulk-register-btn"
                      >
                        {bulkLoading ? <Spinner size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
                        Toplu Kaydet ({selectedDevices.size})
                      </Button>
                    </div>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 cursor-pointer"
                          checked={cloudDevices.devices.filter((d) => !d.registered).length > 0 && selectedDevices.size === cloudDevices.devices.filter((d) => !d.registered).length}
                          onChange={toggleSelectAll}
                          data-testid="bulk-select-all"
                        />
                      </TableHead>
                      <TableHead>Tuya ID</TableHead>
                      <TableHead>{t("admin.cloud_name")}</TableHead>
                      <TableHead>{t("admin.category")}</TableHead>
                      <TableHead>{t("admin.model")}</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>{t("admin.system_record")}</TableHead>
                      <TableHead className="w-24">{t("common.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cloudDevices.devices
                      .filter((d) => cloudFilter === "all" || (cloudFilter === "unregistered" ? !d.registered : d.registered))
                      .map((device) => (
                      <TableRow key={device.tuya_device_id} data-testid={`cloud-device-${device.tuya_device_id}`} className={selectedDevices.has(device.tuya_device_id) ? "bg-blue-50/50" : ""}>
                        <TableCell>
                          {!device.registered && (
                            <input
                              type="checkbox"
                              className="rounded border-slate-300 cursor-pointer"
                              checked={selectedDevices.has(device.tuya_device_id)}
                              onChange={() => toggleDeviceSelection(device.tuya_device_id)}
                              data-testid={`select-device-${device.tuya_device_id}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{device.tuya_device_id}</TableCell>
                        <TableCell className="text-sm">{device.name || "-"}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{device.category || "-"}</Badge></TableCell>
                        <TableCell className="text-xs text-slate-500">{device.model || "-"}</TableCell>
                        <TableCell>
                          <Badge className={device.online ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}>
                            {device.online ? "Online" : "Offline"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {device.registered ? (
                            <div className="flex items-center gap-1">
                              <Check size={14} className="text-emerald-600" />
                              <span className="text-xs text-emerald-700">{device.system_device_name}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <X size={14} className="text-amber-500" />
                              <span className="text-xs text-amber-600">{t("dashboard.unregistered")}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {!device.registered && (
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-xs h-7 px-2"
                              onClick={() => {
                                setRegisterTarget(device);
                                setRegisterName(device.name || "");
                                if (customers.length === 1) setRegisterCustomerId(customers[0].id);
                                setRegisterDialogOpen(true);
                              }}
                              data-testid={`register-device-${device.tuya_device_id}`}
                            >
                              <Plus size={12} className="mr-1" /> {t("common.save")}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Register Device Dialog */}
      <Dialog open={registerDialogOpen} onOpenChange={setRegisterDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("admin.register_device")}</DialogTitle>
            <DialogDescription>
              Tuya Cloud'dan bulunan cihazi bir musteriye atayarak sisteme ekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <p className="text-xs text-blue-600 font-medium">Tuya Device ID</p>
              <p className="text-sm font-mono font-bold text-blue-900">{registerTarget?.tuya_device_id}</p>
              <p className="text-xs text-blue-500 mt-1">Cloud Isim: {registerTarget?.name || "-"} | Kategori: {registerTarget?.category || "-"}</p>
            </div>
            <div className="space-y-2">
              <Label>{t("common.customer")}</Label>
              <Select value={registerCustomerId} onValueChange={setRegisterCustomerId}>
                <SelectTrigger data-testid="register-customer-select">
                  <SelectValue placeholder={t("common.select_customer")} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("admin.device_name")}</Label>
              <Input value={registerName} onChange={(e) => setRegisterName(e.target.value)} placeholder="orn: Daire 1 Sayac" data-testid="register-device-name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegisterDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleRegisterDevice} disabled={registerLoading} data-testid="register-device-submit">
              {registerLoading ? t("billing.saving") : t("dashboard.save_register")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device Status + Alerts + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device Status */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Circuitry size={18} className="text-indigo-600" /> {t("dashboard.device_status")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl" data-testid="online-devices-card">
              <div className="flex items-center gap-2">
                <WifiHigh size={18} className="text-emerald-600" />
                <span className="text-sm font-medium text-emerald-700">Online</span>
              </div>
              <span className="text-xl font-bold text-emerald-700">{stats?.online_devices || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid="offline-devices-card">
              <div className="flex items-center gap-2">
                <WifiSlash size={18} className="text-slate-500" />
                <span className="text-sm font-medium text-slate-600">Offline</span>
              </div>
              <span className="text-xl font-bold text-slate-700">{stats?.offline_devices || 0}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl" data-testid="active-alerts-card">
              <div className="flex items-center gap-2">
                <ShieldWarning size={18} className="text-red-600" />
                <span className="text-sm font-medium text-red-700">{t("dashboard.critical_alert")}</span>
              </div>
              <span className="text-xl font-bold text-red-700">{stats?.active_alerts_count || 0}</span>
            </div>
            <Link to="/alerts">
              <Button variant="outline" className="w-full mt-2 text-sm" data-testid="goto-alerts-btn">
                {t("dashboard.goto_alerts")} <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        {/* Recent Alerts */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ShieldWarning size={18} className="text-red-500" /> {t("dashboard.recent_alerts")}
            </CardTitle>
            <Link to="/alerts" className="text-xs text-indigo-600 hover:text-indigo-700">{t("common.view_all")}</Link>
          </CardHeader>
          <CardContent>
            {(stats?.recent_alerts?.length || 0) === 0 ? (
              <div className="text-center py-6">
                <ShieldWarning size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">{t("dashboard.no_alerts")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.recent_alerts.map((alert, i) => (
                  <div key={alert.id || i} className={`p-3 rounded-xl border text-sm ${
                    alert.severity === "critical" ? "bg-red-50 border-red-200" :
                    alert.severity === "warning" ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"
                  }`} data-testid={`recent-alert-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate flex-1">{alert.device_name}</span>
                      <Badge className={`text-[10px] ml-2 ${
                        alert.severity === "critical" ? "bg-red-100 text-red-700" :
                        alert.severity === "warning" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                      }`}>{alert.severity === "critical" ? t("common.critical") : alert.severity === "warning" ? t("common.warning") : t("common.info")}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate">{alert.message}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <ClockCounterClockwise size={18} className="text-indigo-600" /> {t("dashboard.recent_activity")}
            </CardTitle>
            <Link to="/admin/activity" className="text-xs text-indigo-600 hover:text-indigo-700">{t("common.view_all")}</Link>
          </CardHeader>
          <CardContent>
            {(stats?.recent_activity?.length || 0) === 0 ? (
              <div className="text-center py-6">
                <ClockCounterClockwise size={32} className="mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-500">{t("dashboard.no_activity")}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stats.recent_activity.map((act, i) => (
                  <div key={act.id || i} className="p-3 bg-slate-50 rounded-xl" data-testid={`recent-activity-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-900 truncate">{act.user_name || t("staff.system")}</span>
                      <span className="text-[10px] text-slate-400">
                        {act.created_at ? new Date(act.created_at).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : ""}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 truncate">{act.detail || act.action}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Bildirim Ayarlari */}
      <Card className="border-slate-200 shadow-sm" data-testid="email-settings-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <Gear size={18} className="text-violet-600" /> {t("dashboard.email_settings")}
          </CardTitle>
          <div className="flex gap-2">
            {emailSettings?.has_credentials && (
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={emailTesting}
                onClick={async () => {
                  setEmailTesting(true);
                  try {
                    const res = await axios.post(`${API_URL}/api/admin/email-test`);
                    res.data.success ? toast.success(res.data.message) : toast.error(res.data.message);
                  } catch { toast.error("Test hatasi"); }
                  finally { setEmailTesting(false); }
                }} data-testid="email-test-btn">
                {emailTesting ? <Spinner size={14} className="animate-spin mr-1" /> : null}
                {emailTesting ? t("common.loading") : t("dashboard.test_email")}
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => {
              setEmailForm({
                smtp_email: emailSettings?.smtp_email || "",
                smtp_password: "",
                smtp_host: emailSettings?.smtp_host || "smtp.gmail.com",
                smtp_port: emailSettings?.smtp_port || 587,
                sender_name: emailSettings?.sender_name || "NANOMET",
                enabled: emailSettings?.enabled !== false,
              });
              setEmailEditOpen(true);
            }} data-testid="email-edit-btn">
              <Pencil size={14} className="mr-1" /> {t("common.edit")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {emailSettings ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-xs text-slate-400">{t("dashboard.gmail_addr")}</p>
                <p className="font-mono text-slate-700">{emailSettings.smtp_email || <span className="text-amber-500 italic">{t("dashboard.not_set")}</span>}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("common.password")}</p>
                <p className="font-mono text-slate-700">{emailSettings.smtp_password_masked || "****"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("dashboard.sender_name")}</p>
                <p className="text-slate-700">{emailSettings.sender_name || "NANOMET"}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">{t("common.status")}</p>
                <Badge className={emailSettings.enabled !== false ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                  {emailSettings.enabled !== false ? t("common.active") : t("dashboard.disabled")}
                </Badge>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">Email ayarlari yukleniyor...</p>
          )}

          {/* Email Edit Form */}
          {emailEditOpen && (
            <div className="mt-4 p-4 bg-violet-50 rounded-xl border border-violet-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t("dashboard.gmail_addr")}</Label>
                  <Input className="h-8 text-sm font-mono" value={emailForm.smtp_email}
                    placeholder="ornek@gmail.com"
                    onChange={(e) => setEmailForm({ ...emailForm, smtp_email: e.target.value })}
                    data-testid="email-smtp-email-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">App Password</Label>
                  <Input className="h-8 text-sm font-mono" type="password" value={emailForm.smtp_password}
                    placeholder="Degistirmek icin yeni deger girin"
                    onChange={(e) => setEmailForm({ ...emailForm, smtp_password: e.target.value })}
                    data-testid="email-smtp-password-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("dashboard.sender_name")}</Label>
                  <Input className="h-8 text-sm" value={emailForm.sender_name}
                    onChange={(e) => setEmailForm({ ...emailForm, sender_name: e.target.value })}
                    data-testid="email-sender-name-input" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="email-enabled-toggle">
                  <input type="checkbox" checked={emailForm.enabled}
                    onChange={(e) => setEmailForm({ ...emailForm, enabled: e.target.checked })}
                    className="rounded border-slate-300" />
                  {t("dashboard.email_active")}
                </label>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="bg-violet-600 hover:bg-violet-700" disabled={emailSaving}
                  onClick={async () => {
                    setEmailSaving(true);
                    try {
                      const payload = { ...emailForm };
                      if (!payload.smtp_password) delete payload.smtp_password;
                      await axios.put(`${API_URL}/api/admin/email-settings`, payload);
                      toast.success("Email ayarlari kaydedildi");
                      setEmailEditOpen(false);
                      const res = await axios.get(`${API_URL}/api/admin/email-settings`);
                      setEmailSettings(res.data);
                    } catch { toast.error("Kayit hatasi"); }
                    finally { setEmailSaving(false); }
                  }} data-testid="email-save-btn">
                  {emailSaving ? <Spinner size={14} className="animate-spin mr-1" /> : null}
                  {t("common.save")}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEmailEditOpen(false)}>{t("common.cancel")}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

export default AdminDashboard;
